//orders-page.tsx

import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridRowClassNameParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridToolbar,
  getGridBooleanOperators,
} from "@mui/x-data-grid";
import {
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Badge,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../components/confirm-dialog";
import LoadingBackdrop from "../components/loading-backdrop";
import Page from "../components/page";
import { Order } from "../models/order";
import { ordersApi } from "../services/api/orders";
import { DateTime } from "luxon";
import {
  UpdateRecurringOrderDialog,
  UpdateRecurringOrderMethods,
} from "./order-page";
import { UserContext, ErrorContext } from "../components/app-routes";
import { formatCurrency } from "./currency";
import mixpanel from "mixpanel-browser";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { getCustomLabel } from "../utils/custom-labels";

enum DateRange {
  Next7Days,
  Next30Days,
  RestOfYear,
  Today,
  Tomorrow,
  Custom,
  AllTime,
}

const DateRangeAllTimeFromDate = "2020-01-01";
const DateRangeAllTimeToDate = "2100-01-01";
const ordersEndingSoonBannerSnoozeKey = "ordersEndingSoonBannerSnoozeUntil";

const columns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: "Checkbox",
    renderHeader: () => null,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
  },
  {
    field: "cycleWarning",
    headerName: "Cycle Warning",
    width: 50,
    minWidth: 50,
    sortable: false,
    disableColumnMenu: true,
    type: "boolean",
    filterOperators: getGridBooleanOperators(),
    renderHeader: () => null,
    renderCell: (p: any) => {
      const warn = !!p?.row?.cycleWarning;
      if (!warn) return null;

      return (
        <Badge
          variant="dot"
          color="warning"
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <CalendarMonthIcon
            fontSize="small"
            sx={{ opacity: 0.8 }}
            titleAccess="Recurring order cycle ending soon"
          />
        </Badge>
      );
    },
  },
  {
    field: "orderNumber",
    headerName: "Order #",
    width: 100,
    minWidth: 100,
    align: "center",
    headerAlign: "center",
    renderCell: (p: any) => {
      const value = p?.value ?? "";
      if (value === "" || value === null || value === undefined) return "";

      const text = String(value);

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            width: "100%",
          }}
        >
          <span style={{ fontSize: "0.8rem" }}>{text}</span>
          <Tooltip title="Copy order number">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(text).catch(() => {
                    // ignore clipboard errors
                  });
                }
              }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  },

  { field: "customer", headerName: "Customer", width: 200 },
  {
    field: "harvestDate",
    headerName: "Harvest Date",
    width: 125,
    minWidth: 125,
    renderCell: (p: any) => {
      const raw = p?.row?.harvestDate ?? p?.value ?? null;
      if (!raw) return "";
      const dt = DateTime.fromISO(String(raw));
      if (!dt.isValid) return "";
      return dt.toFormat("LLL d, yyyy").replace(".", ""); // e.g. "Dec 26"
    },
    valueFormatter: (p: any) => {
      const s = p?.value ? String(p.value) : null;
      if (!s) return "";
      const dt = DateTime.fromISO(s);
      if (!dt.isValid) return "";
      return dt.toFormat("LLL d").replace(".", ""); // e.g. "Dec 26"
    },
  },

  { field: "orderTotal", headerName: "Order Total", width: 125, minWidth: 125 },
  {
    field: "endDate",
    headerName: "End Date",
    width: 125,
    minWidth: 125,
    renderCell: (p: any) => {
      const raw = p?.row?.endDate ?? p?.value ?? null;
      if (!raw) return "";
      const dt = DateTime.fromISO(String(raw));
      if (!dt.isValid) return "";
      const text = dt.toFormat("LLL d, yyyy");

      const order: Order | undefined = p?.row?.order;
      const highlight = !!order && isRecurringOrderEndingSoon(order);

      return (
        <span
          style={
            highlight ? { color: "#f53d00ff", fontWeight: 500 } : undefined
          }
        >
          {text}
        </span>
      );
    },

    valueFormatter: (p: any) => {
      const s = p?.value ? String(p.value) : null;
      if (!s) return "";
      const dt = DateTime.fromISO(s);
      if (!dt.isValid) return "";
      return dt.toFormat("LLL d yyyy");
    },
  },
  { field: "products", headerName: "Products", width: 250 },
  { field: "notes", headerName: "Notes", width: 200 },
];

// ---------------- Orders Data Grid ----------------

const OrdersDataGrid = React.memo(
  (props: {
    orders: Order[] | null;
    onSelectionChange?: (ids: number[]) => void;
    filters?: { on?: string };
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
  }) => {
    const history = useHistory();
    const { grower } = useContext(UserContext);
    if (!grower) throw new Error("Invalid UserContext");

    if (!props.orders) return null;

    let filterModel: GridFilterModel | undefined;
    if (props.filters?.on) {
      filterModel = {
        items: [
          { field: "harvestDate", operator: "is", value: props.filters.on },
        ],
      };
    }

    return (
      <StripedDataGrid
        storageKey="orders-grid"
        initialState={{
          columns: {
            columnVisibilityModel: {
              notes: false,
            },
          },
          sorting: {
            sortModel: [
              {
                field: "harvestDate",
                sort: "desc",
              },
            ],
          },
          pagination: {
            paginationModel: {
              page: props.initialPage ?? 0,
              pageSize: 15,
            },
          },
        }}
        columns={columns}
        rows={props.orders.map((o) => ({
          id: Number(o.id), // ✅ normalize
          order: o,
          orderNumber: (o as any).orderNumber ?? Number(o.id),
          cycleWarning: isRecurringOrderEndingSoon(o),
          customer: o.customer.name,
          harvestDate: (() => {
            const raw = (o as any).harvestDate;
            if (!raw) return "";
            if (raw instanceof Date)
              return DateTime.fromJSDate(raw).toISODate();
            const s = String(raw);
            const dt = DateTime.fromISO(s);
            return dt.isValid ? dt.toISODate() : s;
          })(),
          orderTotal: formatCurrency(
            o.orderProducts.reduce(
              (total: number, cV) => cV.price * cV.quantity + total,
              0,
            ),
            grower.CountryISO,
          ),
          endDate: (o as any)?.recur?.endDate ?? "",
          products: [
            ...new Set(o.orderProducts.map((oP) => oP.product?.name)),
          ].join(", "),
          notes: o.notes,
        }))}
        /* Initial state + sorting + pagination */
        getRowId={(row) => row.id}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        /* Filters */
        filterModel={filterModel}
        /* Selection */
        disableRowSelectionOnClick
        disableMultipleRowSelection
        checkboxSelection
        showToolbar
        onRowSelectionModelChange={(m) => {
          let oneId: number | undefined;

          if (Array.isArray(m)) {
            const last = m[m.length - 1];
            if (last !== undefined) oneId = Number(last);
          } else {
            const ids: unknown[] = [];
            (m as any)?.ids?.forEach?.((v: unknown) => ids.push(v));
            const last = ids[ids.length - 1];
            if (last !== undefined) oneId = Number(last);
          }

          props.onSelectionChange?.(oneId !== undefined ? [oneId] : []);
        }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: { allColumns: true, fileName: "orders" },
            printOptions: { disableToolbarButton: true },
          },
        }}
        /* Row classes */
        getRowClassName={(p: GridRowClassNameParams) =>
          p.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        /* Row navigation */
        onRowClick={(params: GridRowParams) =>
          history.push(`/home/orders/${params.id}`)
        }
      />
    );
  },
);

// ---------------- Main Page ----------------

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [totalTrashed, setTotalTrashed] = useState(0);
  const [openTrashRecurringOrderDialog, setOpenTrashRecurringOrderDialog] =
    useState(false);
  const location = useLocation();
  const history = useHistory();
  const { handleError } = useContext(ErrorContext);
  const isMounted = useRef(true);

  const endingSoonCount = useMemo(() => {
    if (!orders) return 0;

    const ids = new Set<string>();

    for (const o of orders) {
      if (!isRecurringOrderEndingSoon(o)) continue;

      const anyOrder: any = o;
      const recurId = anyOrder?.recur?.id;

      if (recurId !== undefined && recurId !== null) {
        ids.add(`recur-${recurId}`);
      } else {
        ids.add(`order-${o.id}`);
      }
    }

    return ids.size;
  }, [orders]);

  const [showEndingSoonOnly, setShowEndingSoonOnly] = useState(false);
  const [hideEndingSoonBanner, setHideEndingSoonBanner] = useState(() => {
    try {
      const raw = window.localStorage.getItem(ordersEndingSoonBannerSnoozeKey);
      if (!raw) return false;
      const until = DateTime.fromISO(raw);
      if (!until.isValid) {
        window.localStorage.removeItem(ordersEndingSoonBannerSnoozeKey);
        return false;
      }
      return until > DateTime.now();
    } catch {
      return false;
    }
  });

  const filteredOrders = useMemo(() => {
    if (!orders) return null;
    if (!showEndingSoonOnly) return orders;
    return orders.filter((o) => isRecurringOrderEndingSoon(o));
  }, [orders, showEndingSoonOnly]);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const [dateRange, setDateRange] = useState(getDateRange(searchParams));

  function getDateRange(searchParams: URLSearchParams): DateRange {
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");
    const dateRangeStorage = window.localStorage.getItem("ordersDateRange");

    if (fromParam && toParam) {
      const from = DateTime.fromISO(fromParam);
      const to = DateTime.fromISO(toParam);
      const today = DateTime.now();
      const tomorrow = today.plus({ day: 1 });

      if (from.hasSame(today, "day") && to.hasSame(today, "day")) {
        return DateRange.Today;
      } else if (from.hasSame(tomorrow, "day") && to.hasSame(tomorrow, "day")) {
        return DateRange.Tomorrow;
      } else if (
        from.hasSame(today, "day") &&
        to.hasSame(today.plus({ day: 30 }), "day")
      ) {
        return DateRange.Next30Days;
      } else if (
        from.hasSame(today, "day") &&
        to.hasSame(today.plus({ day: 7 }), "day")
      ) {
        return DateRange.Next7Days;
      } else if (
        from.hasSame(today, "day") &&
        to.hasSame(today.endOf("year"), "day")
      ) {
        return DateRange.RestOfYear;
      } else if (
        from.equals(DateTime.fromISO(DateRangeAllTimeFromDate)) &&
        to.equals(DateTime.fromISO(DateRangeAllTimeToDate))
      ) {
        return DateRange.AllTime;
      } else {
        return DateRange.Custom;
      }
    } else {
      return dateRangeStorage
        ? parseInt(dateRangeStorage)
        : DateRange.Next30Days;
    }
  }

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const now = DateTime.now();
    if (!searchParams.get("from")) {
      let from: DateTime;
      switch (dateRange) {
        case DateRange.Tomorrow:
          from = now.plus({ day: 1 });
          break;
        case DateRange.AllTime:
          from = DateTime.fromISO(DateRangeAllTimeFromDate);
          break;
        default:
          from = now;
      }
      searchParams.set("from", from.toISODate()!);
    }
    if (!searchParams.get("to")) {
      let to: DateTime;
      switch (dateRange) {
        case DateRange.Next30Days:
          to = now.plus({ days: 30 });
          break;
        case DateRange.Next7Days:
          to = now.plus({ days: 7 });
          break;
        case DateRange.RestOfYear:
          to = now.endOf("year");
          break;
        case DateRange.Today:
          to = now;
          break;
        case DateRange.Tomorrow:
          to = now.plus({ day: 1 });
          break;
        case DateRange.AllTime:
          to = DateTime.fromISO(DateRangeAllTimeToDate);
          break;
        default:
          to = now.plus({ day: 30 });
      }
      searchParams.set("to", to.toISODate()!);
    }
    history.replace({ search: searchParams.toString() });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function getOrders() {
      const from = searchParams.get("from");
      const to = searchParams.get("to");
      if (!from || !to) return;
      setBusy(true);
      try {
        const results = await ordersApi.getAll(from, to);
        if (isMounted.current) setOrders(results);
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }
    getOrders();
  }, [totalTrashed, searchParams, handleError]);

  const handleSelectionChange = (ids: number[]) => {
    setSelected(ids);
  };

  const handleClickTrash = () => {
    if (!orders || selected.length === 0) return;

    const selectedId = Number(selected[0]); // ✅ normalize once
    const order = orders.find((v) => Number(v.id) === selectedId); // ✅ robust match

    if (!order) {
      console.warn("Selected id not found", { selectedId });
      return;
    }
    if (order.recur) {
      setOpenTrashRecurringOrderDialog(true);
      return;
    }
    setDialogOpen(true);
  };

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setDialogOpen(false);
    if (!okTrash) return;
    trashOrder();
  };

  const handleOnCloseTrashRecurringOrderDialog = (
    value?: UpdateRecurringOrderMethods,
  ) => {
    setOpenTrashRecurringOrderDialog(false);
    if (!value) return;
    trashOrder(value);
  };

  const trashOrder = async (value?: UpdateRecurringOrderMethods) => {
    if (!orders || selected.length === 0) throw new Error("Invalid state");

    setBusy(true);
    try {
      const selectedId = Number(selected[0]); // ✅ normalize again for safety
      await ordersApi.trash(
        selectedId,
        value === UpdateRecurringOrderMethods.ThisAndFollowingOrders
          ? { recurring: "thisAndFollowing" }
          : undefined,
      );
      mixpanel.track("Delete Order");
      setTotalTrashed((n) => n + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleFromDateChange = (date: DateTime | null) => {
    if (!date) return;
    const toParam = searchParams.get("to");
    if (!toParam) throw new Error("Invalid state");
    searchParams.set("from", date.toISODate()!);
    history.replace({ search: searchParams.toString() });
  };

  const handleToDateChange = (date: DateTime | null) => {
    if (!date) return;
    const fromParam = searchParams.get("from");
    if (!fromParam) throw new Error("Invalid state");
    searchParams.set("to", date.toISODate()!);
    history.replace({ search: searchParams.toString() });
  };

  const handleChangeDateRange = (event: SelectChangeEvent<DateRange>) => {
    const range = event.target.value as DateRange;
    setDateRange(range);
    window.localStorage.setItem("ordersDateRange", range.toLocaleString());
    const dates = getDatesFromRange(range);
    searchParams.set("to", dates.to);
    searchParams.set("from", dates.from);
    history.replace({ search: searchParams.toString() });
  };

  const page = getSearchParamNumber(history, "page", 0);
  const context = useContext(UserContext);
  const grower = context.grower;

  return (
    <Page title={getCustomLabel(grower ?? null, "orders")}>
      {" "}
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1, // replaces margin juggling
        }}
      >
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/home/orders/add"
          disabled={busy}
        >
          Add
        </Button>
        <IconButton
          aria-label="delete"
          disabled={selected.length === 0}
          onClick={handleClickTrash}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
        <FormControl variant="standard" sx={{ minWidth: 120 }}>
          <Select
            variant="standard"
            value={dateRange}
            onChange={handleChangeDateRange}
          >
            <MenuItem value={DateRange.Next7Days}>Next 7 days</MenuItem>
            <MenuItem value={DateRange.Next30Days}>Next 30 days</MenuItem>
            <MenuItem value={DateRange.RestOfYear}>Rest of Year</MenuItem>
            <MenuItem value={DateRange.Today}>Today</MenuItem>
            <MenuItem value={DateRange.Tomorrow}>Tomorrow</MenuItem>
            <MenuItem value={DateRange.Custom}>Custom</MenuItem>
            <MenuItem value={DateRange.AllTime}>All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Date Pickers */}
      {dateRange === DateRange.Custom && (
        <Box sx={{ mt: 2 }}>
          <MobileDatePicker
            closeOnSelect
            value={DateTime.fromISO(searchParams.get("from") || "")}
            onChange={handleFromDateChange}
            label="From"
            sx={{ mr: 1 }}
            format="MMM d, y"
            slotProps={{
              toolbar: { hidden: true },
              textField: {
                variant: "outlined",
                inputProps: { readOnly: true },
              },
            }}
          />
          <MobileDatePicker
            closeOnSelect
            value={DateTime.fromISO(searchParams.get("to") || "")}
            onChange={handleToDateChange}
            label="To"
            sx={{ mr: 1 }}
            format="MMM d, y"
            slotProps={{
              toolbar: { hidden: true },
              textField: {
                variant: "outlined",
                inputProps: { readOnly: true },
              },
            }}
          />
        </Box>
      )}
      {endingSoonCount > 0 && !hideEndingSoonBanner && (
        <Box
          sx={{
            mt: 2,
            mb: 1,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid #ffe0b2",
            backgroundColor: "#fff3e0",
            typography: "body2",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={() => setShowEndingSoonOnly((prev) => !prev)}
          >
            <span>⚠</span>
            <span>
              {showEndingSoonOnly
                ? endingSoonCount === 1
                  ? "Showing only 1 recurring order nearing the end of its cycle. Click to show all orders."
                  : `Showing only ${endingSoonCount} recurring orders nearing the end of their cycles. Click to show all orders.`
                : endingSoonCount === 1
                  ? "1 recurring order is nearing the end of its cycle. Click to show only this order."
                  : `${endingSoonCount} recurring orders are nearing the end of their cycles. Click to show only these orders.`}
            </span>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              whiteSpace: "nowrap",
              fontSize: 12,
              opacity: 0.8,
            }}
          >
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => {
                const until = DateTime.now().plus({ days: 1 }).toISO();
                window.localStorage.setItem(
                  ordersEndingSoonBannerSnoozeKey,
                  until,
                );
                setHideEndingSoonBanner(true);
              }}
            >
              Dismiss for 1 day
            </Box>
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => setHideEndingSoonBanner(true)}
            >
              Dismiss
            </Box>
          </Box>
        </Box>
      )}
      {/* DataGrid */}
      <Box sx={{ height: "750px", width: "100%", pt: 2 }}>
        <OrdersDataGrid
          orders={filteredOrders}
          initialPage={page}
          onPaginationModelChange={(m) => {
            searchParams.set("page", m.page.toString());
            history.replace({ search: searchParams.toString() });
          }}
          onSelectionChange={setSelected}
        />
      </Box>
      <ConfirmDialog
        title="Trash Orders"
        description="Are you sure you want to trash the selected order?"
        open={dialogOpen}
        onClose={handleTrashDialogClose}
      />
      <UpdateRecurringOrderDialog
        open={openTrashRecurringOrderDialog}
        onClose={handleOnCloseTrashRecurringOrderDialog}
        title="Trash Recurring Order"
      />
      <LoadingBackdrop open={busy} />
    </Page>
  );
}

// ---------------- Helpers ----------------

const isRecurringOrderEndingSoon = (order: Order): boolean => {
  const anyOrder: any = order;
  const recur = anyOrder?.recur;

  // Not a recurring order
  if (!recur) return false;

  const rawEnd = recur.endDate;
  if (!rawEnd) return false;

  const endDate =
    rawEnd instanceof Date
      ? DateTime.fromJSDate(rawEnd)
      : DateTime.fromISO(String(rawEnd));

  if (!endDate.isValid) return false;

  const today = DateTime.now().startOf("day");
  const end = endDate.startOf("day");

  const daysUntilEnd = end.diff(today, "days").days;

  // For now: "ending soon" = within the next 42 days (and not already past)
  return daysUntilEnd <= 42 && daysUntilEnd >= 0;
};

const getDatesFromRange = (range: DateRange): { from: string; to: string } => {
  let from: string, to: string;
  const today = DateTime.now().startOf("day");

  switch (range) {
    case DateRange.Next7Days:
    case DateRange.Custom:
      from = today.toISODate();
      to = today.plus({ days: 7 }).toISODate();
      break;
    case DateRange.Next30Days:
      from = today.toISODate();
      to = today.plus({ days: 30 }).toISODate();
      break;
    case DateRange.RestOfYear:
      from = today.toISODate();
      to = today.endOf("year").toISODate();
      break;
    case DateRange.Today:
      from = today.toISODate();
      to = from;
      break;
    case DateRange.Tomorrow:
      from = today.plus({ day: 1 }).toISODate();
      to = from;
      break;
    case DateRange.AllTime:
      from = DateRangeAllTimeFromDate;
      to = DateRangeAllTimeToDate;
      break;
    default:
      throw new Error("Invalid range");
  }

  return { from, to };
};
